import React, { useState, useContext } from 'react';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { update } from 'ramda';

import EkonomiskAnalysColumns from '../../../EkonomiskAnalysColumns/EkonomiskAnalysColumns';
import EDButton from '../../../../../../../../../BaseComponents/Buttons/Button/EDButton';
import EDIcon from '../../../../../../../../../BaseComponents/Icon/EDIcon';
import { EkonomiskAnalysSideMenuContext } from '../../Context/EkonomiskAnalysSideMenuContext';
import { ekanalys_ekanalys_columns } from '../../../../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import EkonomiskAnalysEditColumnDialog from './EditColumn/Components/EkonomiskAnalysEditColumnDialog/EkonomiskAnalysEditColumnDialog';
import {
  getNewColumn,
  newModalColumn,
} from './EditColumn/Utils/EkonomiskAnalysEditColumnUtils';
import { IColumn } from '../../../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import Logger from '../../../../../../../../../Utils/Logger';
import EkonomiskAnalysColumnSelectDialog from './SelectColumn/EkonomiskAnalysColumnSelectDialog';
import EDMenuButton from '../../../../../../../../../BaseComponents/Buttons/MenuButton/EDMenuButton';
import MenuButtonItem from '../../../../../../../../../BaseComponents/Buttons/MenuButton/EDMenuButtonItem';
import EDListItemIcon from '../../../../../../../../../BaseComponents/List/Components/EDListItemIcon';
import EDListItemText from '../../../../../../../../../BaseComponents/List/Components/EDListItemText';
import { IColumnTemplate } from './Queries/AvailableColumnTemplatesHandler';
import { setVandTeckenIfResultColumn } from './SelectColumn/Utils/EkonomiskAnalysColumnSelect.utils';
import { ISelectedAnalys } from '../../../../EkonomiskAnalysPage';

interface IProps {
  availableColumnTemplates: IColumnTemplate[];
  selectedAnalys: ISelectedAnalys | null;
  menuButton?: boolean;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      container: {
        paddingLeft: 40,
        paddingRight: 40,
        paddingTop: 16,
        paddingBottom: 16,
      },
      pickKolumnerButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginRight: -8,
      },
    } as const)
);

/**
 * Vyn över period och kolumner i sidomenyn
 */
const EkonomiskAnalysPeriodKolumner: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles();
  const [columnSelectOpen, setColumnSelectOpen] = useState(false);
  const [editColumnDialogOpen, setEditColumnDialogOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState<
    ekanalys_ekanalys_columns | undefined
  >(undefined);

  const onColumnClick = (clickedColumn: ekanalys_ekanalys_columns) => {
    setSelectedColumn(clickedColumn);
    setEditColumnDialogOpen(true);
  };

  const closeEditColumnDialog = () => {
    setEditColumnDialogOpen(false);
  };

  const editColumnDialogOnExited = () => {
    setSelectedColumn(undefined);
  };

  const setColumn =
    (
      oldColumns: IColumn[],
      onColumnsChanged: (newColumns: IColumn[]) => void
    ) =>
    (
      newColumn: ekanalys_ekanalys_columns,
      newName: string,
      newDisplayLabel: string
    ) => {
      if (!selectedColumn) {
        setEditColumnDialogOpen(false);
        onColumnsChanged([
          ...oldColumns,
          {
            name: newName,
            id:
              oldColumns.length > 0
                ? oldColumns[oldColumns.length - 1].id + 1
                : 0,
            displayLabel: newDisplayLabel,
            data: newColumn,
          },
        ]);
      } else {
        const oldColumnIndex = oldColumns.findIndex(
          col => col.data.LOPNR === newColumn.LOPNR
        );

        if (oldColumnIndex === -1) {
          Logger.error(
            new Error(
              `Could not find column with LOPNR: ${newColumn.LOPNR} in oldColumns`
            )
          );
        } else {
          if (!newColumn.CODE_CAPTION) {
            Logger.error(
              new Error(
                `Tried to update columns with a CODE_CAPTION of: ${newColumn.CODE_CAPTION}`
              )
            );
          } else {
            const newColumns: IColumn[] = update(
              oldColumnIndex,
              {
                name: newName,
                id: oldColumns[oldColumnIndex].id,
                displayLabel: newDisplayLabel,
                data: newColumn,
              },
              oldColumns
            );

            // Close dialog
            setSelectedColumn(undefined);
            setEditColumnDialogOpen(false);
            onColumnsChanged(newColumns);
          }
        }
      }
    };

  const toggleColumnSelect = () => {
    setColumnSelectOpen(prevOpen => !prevOpen);
  };

  const {
    newAnalysSettings,
    getPeriodsComponent,
    onColumnsChanged,
    selectedAnalys,
    isLopandeAnalys,
  } = useContext(EkonomiskAnalysSideMenuContext);

  const dateError =
    newAnalysSettings.dates.from.error ||
    (newAnalysSettings.isInterval && newAnalysSettings.dates.to.error);

  let disabledReason = '';
  if (!isLopandeAnalys) {
    disabledReason = 'Välj kolumner ej tillgänglig för radbaserade analyser';
  } else if (dateError) {
    disabledReason = 'Ogiltig period';
  }

  const reversedTemplates = props.availableColumnTemplates.reduce<
    IColumnTemplate[]
  >((a, b) => {
    a.push({ ...b, columns: b.columns.map(setVandTeckenIfResultColumn(true)) });
    return a;
  }, []);

  const columnTemplates = [
    <MenuButtonItem
      key="addOption"
      onClick={() => setEditColumnDialogOpen(true)}
    >
      <EDListItemIcon>
        <EDIcon color="action" iconType="add" />
      </EDListItemIcon>
      <EDListItemText primaryTypographyProps={{ variant: 'body2' }}>
        Lägg till enskild kolumn
      </EDListItemText>
    </MenuButtonItem>,
    ...props.availableColumnTemplates.map(x => (
      <MenuButtonItem
        key={x.id}
        category="Kolumnmallar"
        onClick={() => onColumnsChanged(x.columns)}
      >
        <EDListItemText primaryTypographyProps={{ variant: 'body2' }}>
          {x.name}
        </EDListItemText>
      </MenuButtonItem>
    )),
    ...reversedTemplates.map(x => (
      <MenuButtonItem
        key={`${x.id}-reversed`}
        category="Teckenvända mallar"
        onClick={() => onColumnsChanged(x.columns)}
      >
        <EDListItemText primaryTypographyProps={{ variant: 'body2' }}>
          {x.name}
        </EDListItemText>
      </MenuButtonItem>
    )),
  ];

  return (
    <div className={classes.container}>
      {getPeriodsComponent(undefined, undefined, true)}
      <EDMenuButton
        icon="folder_open"
        tooltip={'Välj kolumnmall'}
        buttonLabel="Välj kolumnmall"
        primary
        positionRight
        disabled={!isLopandeAnalys}
        disabledReason={
          'Välj kolumnmall ej tillgänglig för radbaserade analyser'
        }
        iconColor="inherit"
      >
        {columnTemplates}
      </EDMenuButton>
      <EkonomiskAnalysColumns
        columns={newAnalysSettings.columns}
        onUpdate={onColumnsChanged}
        onColumnClick={onColumnClick}
        disableColumnEdit={Boolean(dateError)}
      />
      <EkonomiskAnalysEditColumnDialog
        title="Redigera kolumn"
        open={editColumnDialogOpen}
        codePrivilegeId={selectedAnalys ? selectedAnalys.codePrivilege.id : ''}
        analysName={selectedAnalys ? selectedAnalys.analys.id : ''}
        close={closeEditColumnDialog}
        onExited={editColumnDialogOnExited}
        column={
          selectedColumn != null
            ? selectedColumn
            : newModalColumn(getNewColumn()).data
        }
        setColum={setColumn(newAnalysSettings.columns, onColumnsChanged)}
        dates={newAnalysSettings.dates}
        isInterval={newAnalysSettings.isInterval}
        isNewColumn={selectedColumn == null}
      />
      <div className={classes.pickKolumnerButtonContainer}>
        <EDButton
          label="Välj kolumner"
          icon={
            <EDIcon iconType="view_column" color="inherit" marginRight={1} />
          }
          onClick={toggleColumnSelect}
          flat
          primary
          padding={1}
          labelNotUpperCase
          lowEmphasis
          disabled={!isLopandeAnalys || Boolean(dateError)}
          disabledReason={disabledReason}
        />
      </div>
      <EkonomiskAnalysColumnSelectDialog
        selectedColumns={newAnalysSettings.columns}
        dates={newAnalysSettings.dates}
        dialogOpen={columnSelectOpen}
        dialogOnClose={toggleColumnSelect}
        onUpdate={onColumnsChanged}
        isInterval={newAnalysSettings.isInterval}
        availableColumnTemplates={props.availableColumnTemplates}
        selectedAnalys={props.selectedAnalys}
      />
    </div>
  );
};

export default EkonomiskAnalysPeriodKolumner;
